import type dagre from '@dagrejs/dagre';
import type { InitialEdgeData } from '../parse-graph';

/** Dagre is horrible and unintelligible. During its layouting process, it inserts all sorts of fake nodes in intermediate steps. Given an edge, this determines the first real `nodeId` from following this edge, effectively telling us the true target of this edge */
const findTrueTargetNodeId = (
  g: dagre.graphlib.Graph,
  edges: InitialEdgeData[],
  edge: { v: string; w: string },
): string | undefined => {
  const trueNodeIds = edges.flatMap(e => [e.source, e.target]);
  if (trueNodeIds.includes(edge.w)) {
    return edge.w;
  }
  for (const outEdge of g.outEdges(edge.w) || []) {
    const child = findTrueTargetNodeId(g, edges, outEdge);
    if (child) {
      return child;
    }
  }
};

/**
 * This is a copy of the default ordering function in `dagre` found [here](https://github.com/dagrejs/dagre/blob/00d3178d671e49de9c032e3abd281dc9f2739e73/lib/order/init-order.js#L18) with some improvements to respect our ordering of edges from nodes that have multiple children.
 *
 * This takes in the `edges` from our graph and uses their metadata to decide the order of nodes within a rank.
 *
 * This is effectively renders split nodes' branches from left to right in the order in which they are executed.
 */
const computeOrderForRanks = (g: dagre.graphlib.Graph, edges: InitialEdgeData[]) => {
  const visited: Record<string, boolean> = {};
  const simpleNodes = g.nodes().filter(v => !g.children(v)?.length);
  const simpleNodesRanks = simpleNodes.map(v => g.node(v).rank || 0);
  const maxRank = Math.max(...simpleNodesRanks);

  /** This is a 2d array where each row is a list of ordered nodes within one rank. */
  const layers: string[][] = [];
  for (let i = 0; i <= maxRank; i++) {
    layers.push([]);
  }

  function dfs(nodeId: string) {
    if (visited[nodeId]) return;
    visited[nodeId] = true;
    const node = g.node(nodeId);
    layers[node.rank || 0].push(nodeId);

    // Where dagre's default implementation follows edges of this node in an arbitrary order,
    // we here use the `edgeIndex` metadata to sort the edges of this node.
    // When we traverse the graph with `dfs`, we first follow the earliest edges.
    const orderedOutEdges = g
      .outEdges(nodeId)
      ?.map(e => {
        const trueTargetNodeId = findTrueTargetNodeId(g, edges, e);
        if (!trueTargetNodeId) {
          throw new Error(`Child not found for edge ${e.v} -> ${e.w}`);
        }
        const edge = edges.find(edge => edge.source === e.v && edge.target === trueTargetNodeId);
        const edgeOrder = edge?.data.edgeIndex;

        return { target: e.w, edgeOrder: edgeOrder || 0 };
      })
      .sort((a, b) => a.edgeOrder - b.edgeOrder);

    orderedOutEdges?.forEach(e => {
      dfs(e.target);
    });
  }

  // Start running DFS from the root node(s) with the lowest rank.
  const orderedVs = simpleNodes.sort((a, b) => (g.node(a).rank || 0) - (g.node(b).rank || 0));
  orderedVs.forEach(dfs);

  return layers;
};

export const setNodeOrderWithinRanks = (g: dagre.graphlib.Graph, edges: InitialEdgeData[]) => {
  if (g.nodes().length === 0) {
    return;
  }
  const layers = computeOrderForRanks(g, edges);
  // Set the order of each node
  Object.values(layers).forEach(layer => {
    layer.forEach((v: string, i: number) => {
      // @ts-expect-error dagre types are incorrect
      g.node(v).order = i;
    });
  });
};
